import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useDate from '../../hooks/useDate';
import Calendar from '../calendar/calendar';
import OutsideClick from '../outside-click/outside-click';

/**
 * A date field that selects an entire Mon–Sun week at once.
 * On selection it sets both `startDate` and `endDate` in the Formik form context.
 */
const WeekPickerField = ({ onWeekDatesChange }: { onWeekDatesChange?: (start: Date, end: Date) => void }) => {
    const { translate } = useLocale();
    const { setFieldValue, values } = useFormikContext<any>();
    const [visible, setVisible] = useState(false);
    const [weekStart, setWeekStart] = useState<Date | null>(values.startDate instanceof Date ? values.startDate : null);
    const [weekEnd, setWeekEnd] = useState<Date | null>(values.endDate instanceof Date ? values.endDate : null);
    const [anchorDate, setAnchorDate] = useState<Date>(weekStart ?? new Date());
    const { matrix, decMonth, incMonth, decYear, incYear, today } = useDate(anchorDate, anchorDate, setAnchorDate);

    const handleWeekSelect = (monday: Date, sunday: Date) => {
        setWeekStart(monday);
        setWeekEnd(sunday);
        setFieldValue('startDate', monday);
        setFieldValue('endDate', sunday);
        onWeekDatesChange?.(monday, sunday);
        setVisible(false);
    };

    const formatWeek = () => {
        if (!weekStart || !weekEnd) {
            return translate.byKey(TranslationKeys.selectDate);
        }
        const fmt = (d: Date) => d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
        return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
    };

    return <div className="flex flex-col w-full">
        <div className="flex flex-row w-full justify-between items-center cursor-pointer" onClick={() => setVisible(true)}>
            <span className={`p-1 text-sm bg-gray-input w-full ${weekStart ? 'text-gray-800' : 'text-gray-500'}`}>
                {formatWeek()}
            </span>
        </div>
        <div className="relative">
            {visible && (
                <OutsideClick onClick={() => setVisible(false)}>
                    <div className="flex flex-row shadow-2xl border w-auto justify-between items-center text-xs mt-1 p-1 bg-white z-50 fixed">
                        <Calendar
                            date={anchorDate}
                            matrix={matrix}
                            setDate={setAnchorDate}
                            decMonth={decMonth}
                            incMonth={incMonth}
                            decYear={decYear}
                            incYear={incYear}
                            today={today}
                            onWeekSelect={handleWeekSelect}
                            weekStart={weekStart}
                            weekEnd={weekEnd}
                        />
                    </div>
                </OutsideClick>
            )}
        </div>
    </div>
};

export default WeekPickerField;
